import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, Lock, LogIn, AlertCircle } from "lucide-react";

/**
 * Split-screen layout mirroring Grupo Vértice | Control R.H:
 * left dark side with brand + hero copy, right cream side with login form.
 */
export default function Login() {
  const { login, googleSession, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.substring(1)).get("session_id");
      if (sid) {
        setGoogleLoading(true);
        googleSession(sid).then((r) => {
          setGoogleLoading(false);
          if (r.ok) {
            window.history.replaceState(null, "", window.location.pathname);
            navigate("/dashboard", { replace: true });
          } else {
            setError(r.error || "No se pudo iniciar con Google");
            window.history.replaceState(null, "", window.location.pathname);
          }
        });
      }
    }
  }, [googleSession, navigate]);

  useEffect(() => {
    if (user && user.id) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) navigate("/dashboard", { replace: true });
    else setError(r.error);
  };

  const startGoogle = () => {
    const redirect = encodeURIComponent(`${window.location.origin}/login`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left — dark wine */}
      <aside className="hero-dark relative hidden lg:flex flex-col justify-between p-12 xl:p-16 text-[#F1EDE4]">
        <div>
          <Logo variant="light" size="lg" />
          <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-[#C19B6C] ml-[68px]">
            Sistema para Despacho Legal
          </div>
        </div>
        <div>
          <h1 className="font-display text-5xl xl:text-6xl leading-[0.98] text-[#F1EDE4]">
            Su despacho, <span className="text-[#C19B6C]">en orden.</span>
          </h1>
          <p className="mt-6 max-w-lg text-[#D4C7B8]">
            Gestión integral de casos, procesos judiciales, notarial y cobros para bufetes costarricenses.
          </p>
          <ul className="mt-10 space-y-3 text-sm">
            {[
              "Expedientes judiciales completos",
              "Escrituras, protocolos y tomos notariales",
              "Facturación, honorarios y cobros",
              "Archivo digital con permisos por rol",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-[#C19B6C]" />
                <span className="text-[#EBE0CE]">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A7563]">
          © {new Date().getFullYear()} · San José, Costa Rica
        </div>
      </aside>

      {/* Right — cream form */}
      <main className="paper flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo variant="dark" /></div>

          <h2 className="font-display text-3xl text-[#1A0810]">Bienvenido de vuelta</h2>
          <p className="mt-1 text-sm text-[#6B5D57]">Inicie sesión en su cuenta</p>

          <Button
            type="button"
            onClick={startGoogle}
            disabled={googleLoading}
            data-testid="google-login-button"
            variant="outline"
            className="mt-8 w-full h-12 bg-white hover:bg-[#FBF9F4] border-[#E5DFD3] text-[#1A0810] font-medium"
          >
            {googleLoading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verificando...</>
            ) : (
              <>
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuar con Google
              </>
            )}
          </Button>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-[#E5DFD3]" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-[0.22em]">
              <span className="bg-[#F1EDE4] px-3 text-[#6B5D57]">o con email</span>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D57] font-semibold">Correo electrónico</label>
              <div className="mt-2 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
                <Input
                  type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="h-12 pl-10 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]"
                  placeholder="usted@bufete.cr"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D57] font-semibold">Contraseña</label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
                <Input
                  type="password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="h-12 pl-10 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]"
                  placeholder="••••••••"
                />
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-[#C25A5A] hover:text-[#7A1E2E] font-medium"
                >
                  ¿Olvidó su contraseña?
                </Link>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-center gap-2 text-sm text-[#7A1E2E] bg-[#FBEDEF] border border-[#7A1E2E]/25 rounded-md px-3 py-2"
              >
                <AlertCircle className="h-4 w-4" strokeWidth={1.8} />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <><LogIn className="mr-2 h-4 w-4" strokeWidth={2} /> Iniciar sesión</>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-[#6B5D57]">
            ¿No tiene cuenta?{" "}
            <Link to="/register" data-testid="go-to-register-link" className="text-[#C25A5A] hover:text-[#7A1E2E] font-medium">
              Crear una
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
